import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, errorText } from '../api/client'
import type { MailDesign, MailTemplate } from '../api/types'
import { useAsync } from '../lib/useAsync'
import { Icon } from './Icon'
import { Modal } from './Modal'
import { Loading } from './States'
import { useToast } from './Toast'

/** Mail this person a piece, from here.
 *
 *  Modelled on the lead platform's own mail box, which was better than a shrunken
 *  PDF in a panel: you see what the piece says, who it is going to, and the two
 *  decisions that matter — which piece, and queue it. The real thing opens full
 *  size in its own tab, because that is the only size at which anyone can actually
 *  read a letter.
 *
 *  Both your saved pieces and the templates are listed, so an officer who has
 *  never opened the builder still has four things to send. Queuing a template
 *  saves it as a design on the way through. */

type Choice =
  | { kind: 'design'; id: string; name: string; what: string; design: MailDesign }
  | { kind: 'template'; id: string; name: string; what: string; template: MailTemplate }

const fieldsOf = (t: MailTemplate): Record<string, string> =>
  Object.fromEntries(t.blocks.map((b) => [b.key, b.default]))

export function MailToContactModal({ contactId, name, onClose, onQueued }: {
  contactId: number
  name: string
  onClose: () => void
  onQueued?: () => void
}) {
  const { data, loading } = useAsync(() => api.mailDesigns(), [])
  const { data: contact } = useAsync(() => api.contact(contactId), [contactId])
  const [picked, setPicked] = useState<Choice | null>(null)
  const [busy, setBusy] = useState(false)
  const [proofing, setProofing] = useState(false)
  const nav = useNavigate()
  const toast = useToast()

  // Where a piece would actually go. Nothing here means nothing can be queued, and
  // a proof has to fall back to our own address for Lob to render one at all.
  const where = [contact?.address, [contact?.city, contact?.state].filter(Boolean).join(', '),
                 contact?.zip].filter(Boolean)

  const choices: Choice[] = [
    ...(data?.designs ?? []).map((d): Choice => ({
      kind: 'design', id: `d${d.id}`, name: d.name, design: d,
      what: `${d.kind} · yours`,
    })),
    // A template you have already saved a design from is that design — listing both
    // puts the same name on the list twice and makes the officer guess which.
    ...(data?.templates ?? [])
      .filter((t) => !(data?.designs ?? []).some((d) => d.template_key === t.key))
      .map((t): Choice => ({
        kind: 'template', id: `t${t.key}`, name: t.name, template: t,
        what: `${t.kind} · ready to send`,
      })),
  ]
  useEffect(() => { if (!picked && choices.length) setPicked(choices[0]!) }, [choices.length])

  const body = picked && (picked.kind === 'design'
    ? { name: picked.design.name, kind: picked.design.kind, source: picked.design.source,
        template_key: picked.design.template_key, layout: picked.design.layout,
        color: picked.design.color, fields: picked.design.fields }
    : { name: picked.template.name, kind: picked.template.kind, source: 'template' as const,
        template_key: picked.template.key, layout: picked.template.layout,
        color: null, fields: fieldsOf(picked.template) })


  /** A design id for the picked piece, creating one from a template if need be —
   *  the same thing queueing does, so proofing a template doesn't lose it. */
  async function designId(): Promise<number | null> {
    if (!picked) return null
    if (picked.kind === 'design') return picked.design.id
    // Reuse the one this template already made. Otherwise every queue and every
    // proof leaves another "Home equity letter" behind, and the Mail page fills up
    // with copies of the same piece.
    const already = (data?.designs ?? []).find(
      (d) => d.source === 'template' && d.template_key === picked.template.key)
    if (already) return already.id
    const made = await api.createMailDesign({
      name: picked.template.name, kind: picked.template.kind, source: 'template',
      template_key: picked.template.key, layout: picked.template.layout,
      fields: fieldsOf(picked.template),
    })
    return made.id
  }

  /** The proof PDF Lob renders — the actual printed artefact, opened in a tab.
   *  Free: it goes through the test key however the piece was made. */
  async function openFullSize() {
    setProofing(true)
    try {
      const id = await designId()
      if (id === null) return
      // With the contact, so the PDF reads as it will for them rather than for
      // the builder's sample name.
      const res = await api.proofMailDesign(id, contactId)
      if (!res.url) return toast('Lob returned no proof for that piece', { tone: 'warning' })
      if (!window.open(res.url, '_blank')) toast('Allow pop-ups to open the proof', { tone: 'warning' })
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    } finally {
      setProofing(false)
    }
  }

  async function queue() {
    if (!picked) return
    setBusy(true)
    try {
      const id = await designId()
      if (id === null) return
      const res = await api.sendMailDesign(id, [contactId], false)
      const refused = res.refused[0]
      if (res.sent.length) {
        toast(`“${picked.name}” queued for ${name}`, { tone: 'positive' })
        onQueued?.()
        onClose()
      } else {
        toast(refused ? `Not queued — ${refused.reason}` : 'Nothing was queued', { tone: 'warning' })
      }
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    } finally {
      setBusy(false)
    }
  }

  const fields = body?.fields ?? {}

  return (
    <Modal open width={620} title={`Direct mail · ${name}`} onClose={onClose}
      subtitle="Pick a piece, see where it goes, and queue it."
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          {/* Greyed only once we know there is nowhere to post it — not while the
              contact is still loading, which read as "this never works". */}
          <button className="btn primary" onClick={queue}
            disabled={!picked || busy || (!!contact && !where.length)}
            title={contact && !where.length ? 'No mailing address on file' : undefined}>
            <Icon name="local_post_office" size={16} /> {busy ? 'Queueing…' : 'Queue for mailing'}
          </button>
        </>
      }>
      {loading ? <Loading /> : (
        <div className="stack">
          <div className="mf-pills">
            {choices.map((c) => (
              <button key={c.id} className={`mf-pill${picked?.id === c.id ? ' on' : ''}`}
                onClick={() => setPicked(c)} title={c.what}>
                <Icon name={c.what.startsWith('postcard') ? 'contact_mail' : 'local_post_office'} size={13} />
                {c.name}
              </button>
            ))}
          </div>

          {/* The piece as a piece: what it says, who it goes to, what it carries. */}
          <div className="mail-card">
            <div className="mail-card-band">
              <span>Buckingham <b>Mortgage</b></span>
              <span className="mail-card-class">FIRST-CLASS</span>
            </div>
            <div className="mail-card-body">
              <div className="mail-card-copy">
                <div className="mail-card-hl">{fields.headline || picked?.name}</div>
                {fields.subhead ? <div className="mail-card-sub">{fields.subhead}</div> : null}
                {fields.cta ? <div className="mail-card-cta">{fields.cta}</div> : null}
              </div>
              {/* Lob proofs the piece as addressed, so with nowhere to post it there
                  is nothing to render — greyed for the same reason Queue is, rather
                  than failing at Lob with a 422 after the click. */}
              <button className="mail-card-open" onClick={openFullSize}
                disabled={!picked || proofing || !where.length}
                title={where.length ? 'Open the printed proof'
                  : 'No mailing address on file — there is nothing to proof'}>
                <Icon name="picture_as_pdf" size={15} />
                {proofing ? 'Proofing…' : 'Read it full size'}
              </button>
            </div>
            <div className="mail-card-to">
              <span className="mail-card-tag">Mail to</span>
              <strong>{name}</strong>
              {where.length ? where.map((line) => <div key={line}>{line}</div>)
                : <div className="mail-card-nowhere">No mailing address on file — nothing can be queued.</div>}
            </div>
            <div className="mail-card-foot">
              Buckingham Mortgage LLC · NMLS #1124970 · Equal Housing Lender
            </div>
          </div>

          <p className="quiet mail-card-note">
            <Icon name="shield" size={14} />
            <span>Direct mail needs no opt-in — it isn’t TCPA or CAN-SPAM. Printed and posted
              first class, tracked, and the delivery scan comes back to their activity. Every
              piece carries the NMLS line, the Equal Housing mark and the opt-out notice.</span>
          </p>
        </div>
      )}

      <button className="card wf-choice mail-pick-new" onClick={() => nav(`/mail?contact=${contactId}`)}>
        <Icon name="add" size={20} style={{ color: 'var(--accent)' }} />
        <span>
          <span style={{ fontWeight: 600, display: 'block' }}>Create a new mail piece</span>
          <span className="quiet" style={{ fontSize: 12.5 }}>
            Opens the builder with {name} as the recipient — write it, then Send.
          </span>
        </span>
      </button>
    </Modal>
  )
}
